import React, {useEffect, useRef, useState} from 'react';
import {useEdges, useNodes, useReactFlow} from "@xyflow/react";
import {Types} from "../enums.js";
import {useEdgeBuilder} from "../../association-network-context.jsx";
import projectUtils, {clearRequiredFieldError, markRequiredFields} from "../../../../../../utils/project-utils.js";
import platePatternUtils from "../../../../../../utils/plate-pattern-utils.js";
import Button from "../../../../../../components/button/button.jsx";
import InputForm from "../../../../../../components/input/input-form.jsx";
import associationNetworkService from "../../../../../../services/web/association-network-service.js";
import {toast} from "react-toastify";
import {getLayoutedElements} from "../utils.js";
import I18nKeys from "../i18n-keys.js";
import {getAssetPath} from "../../../../../../utils/get-asset-path.js";
import {Modal} from "../../../../../../components/modal/modal.jsx";
import {FaBuilding, FaClipboardList, FaImage, FaNewspaper} from "react-icons/fa";
import {MaskDefault} from "../../../../../../utils/mask.js";
import {FaShareNodes} from "react-icons/fa6";
import SentryRow from "../../../../../../components/template/sentry-row.jsx";
import SelectComponent from "../../../../../../components/select/select-component.jsx";
import InputTextarea from "../../../../../../components/input/input-textarea.jsx";

const socialMedias = [
    { value: "Facebook", label: "Facebook" },
    { value: "Instagram", label: "Instagram" },
    { value: "X (Twitter)", label: "X (Twitter)" },
    { value: "LinkedIn", label: "LinkedIn" },
    { value: "TikTok", label: "TikTok" },
]

const items = [
    {name: "Indivíduos", icon: "sw.individual.svg", id: Types.INDIVIDUAL},
    {name: "Veículos", icon: "sw.plate.svg", id: Types.VEHICLE},
    {name: "Abordagens", icon: "sw.approach.svg", id: Types.ENCOUNTER},
    {name: "Ocorrências", icon: "sw.occurrence.svg", id: Types.OCCURRENCE},
    {name: "SOS", icon: "sw.sos.svg", id: Types.SOS},
    {name: "", icon: "sw.bogcm.svg", id: Types.BO},
    {name: "Passagem", icon: "sw.vehicle.svg", id: Types.PASSAGE},
    {name: "Empresa", icon: "", id: Types.COMPANY, fontIcon: <FaBuilding size={32} color={"#031b34"}/>},
    {name: "Rede social", icon: "", id: Types.SOCIAL_MEDIA, fontIcon: <FaShareNodes size={32} color={"#031b34"}/>},
    {name: "Notícia", icon: "", id: Types.NEWS, fontIcon: <FaNewspaper size={32} color={"#031b34"}/>},
    {name: "Imagem", icon: "", id: Types.IMAGE, fontIcon: <FaImage size={32} color={"#031b34"}/>},
    {name: "Anotação", icon: "", id: Types.ANNOTATION, fontIcon: <FaClipboardList size={32} color={"#031b34"}/>},
]

const initialState = {
    social_media: "",
    additional_value: "",
    value: "",
    type: Types.INDIVIDUAL,
}

const Search = ({show, cpf, plate, passage}) => {
    const {setPanelOpened, boName, setIsLoading, setImages} = useEdgeBuilder()
    const inputRef = useRef(null);
    const [imageFile, setImageFile] = useState()
    const nodes = useNodes();
    const edges = useEdges();
    const [item, setItem] = useState(initialState)
    const [tab, setTab] = useState("individual")
    const {setNodes, setEdges, fitView} = useReactFlow();
    const reactFlowInstance = useReactFlow();
    const imageRequiredRef = useRef(null);

    useEffect(() => {
        if (show) setTimeout(() => inputRef.current?.focus(), 0)
    }, [show]);

    useEffect(() => {
        inputRef.current?.focus()
    }, [tab]);

    const onVehicleInputChange = (e) => {
        const {value} = e.target;
        const formattedValue = platePatternUtils.formatPlate(value);
        setItem(prev => ({...prev, value: formattedValue}))
    }

    const onChange = ({target: {value}}) => setItem(prev => ({...prev, value}))
    const onChangeAdditional = ({target: {value}}) => setItem(prev => ({...prev, additional_value: value}))

    useEffect(() => {
        let newItem;

        if (cpf) newItem = { value: cpf, type: Types.INDIVIDUAL}
        if (plate) newItem = { value: plate, type: Types.VEHICLE}
        if (passage) {
            const base64_decode = atob(passage);
            const passageData = JSON.parse(base64_decode);

            newItem = {value: passageData.plate, type: Types.PASSAGE, passage: passageData}
        }
        if (newItem) init(newItem)

    }, [cpf, plate]);

    useEffect(() => {
        if (!show) {
            setItem(initialState)
            setImageFile(undefined)
        }
    }, [show]);

    useEffect(() => {
        clearRequiredFieldError(imageRequiredRef.current)
    }, [imageFile]);

    const setTabOnClick = (id) => {
        setTab(id)
        setItem(prev => ({...prev, type: id, value: "", social_media: "", additional_value: ""}))
    }

    const init = (newItem) => {
        const invalidFields = markRequiredFields("", {focus: false});
        if (invalidFields.length) {
            projectUtils.swalErrorMessage("", "Preencha os campos obrigatórios.", "Ok");
            return;
        }

        setIsLoading(true)

        const formData = new FormData();
        let value = newItem?.value || item.value;
        let passage = newItem?.passage ? JSON.stringify(newItem.passage) : null

        if (item && item.type === Types.PASSAGE) {
            const [procId, captureTime, plate] = item.value.split(";");

            value = plate;
            passage = JSON.stringify({plate, captureTime, procId});
        }

        formData.append("type", newItem?.type || item.type);
        formData.append("value", value);
        formData.append("additionalValue", newItem?.additional_value || item.additional_value);
        formData.append("socialMedia", newItem?.social_media || item.social_media);
        if (passage) formData.append("passage", passage)

        associationNetworkService.init(formData)
            .then(async ({data: {init, analysis}}) => {
                let baseNode, id;

                if (init) {
                    const {data, type} = init;

                    if (item.type === Types.BO && data === null) {
                        projectUtils.swalErrorMessage('', 'Apenas BOs com status \'Aprovado\' são permitidos na análise.', 'Ok')
                        return;
                    }

                    id = init.id

                    const existNode = nodes.find(n => n.id === id);
                    if (existNode) {
                        await reactFlowInstance.fitView({nodes: [existNode], padding: 0.5, duration: 800});
                        toast.warning('Elemento já adicionado na rede.');
                        setPanelOpened(null)
                        return;
                    }

                    baseNode = {id, type, data, position: {x: 0, y: 0}}

                    if (item.type === Types.IMAGE) {
                        setImages(prev => [...prev, {id, file: imageFile}])
                    }

                } else if ([Types.INDIVIDUAL, Types.VEHICLE].includes(item.type)) {
                    id = item.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
                    baseNode = {
                        id,
                        type: item.type,
                        data: {cpf: id, plate: id},
                        position: {x: 0, y: 0}
                    }
                } else {
                    projectUtils.swalMessage("warning", "", "Nenhum resultado encontrado", "Ok")
                }

                if (baseNode) {
                    let newNodes = []
                    let newEdges = []
                    if (analysis) {
                        newNodes = analysis.nodes.filter(d => !nodes.some(n => n.id === d.id));
                        newEdges = analysis.edges.filter(d => !edges.some(n => n.id === d.id)).map(e => ({
                            ...e,
                            type: 'floating'
                        }));
                    }

                    const layoutedNodes = getLayoutedElements({
                        newNodes: [baseNode, ...newNodes],
                        newEdges,
                        fixedNode: baseNode,
                        existingNodes: nodes,
                    });

                    setNodes(prev => [...prev, ...layoutedNodes]);
                    setEdges(prev => [...prev, ...newEdges]);

                    setPanelOpened(null);

                    await fitView({nodes: [baseNode], padding: 1, duration: 800});
                }
            })
            .catch((err) => {
                console.error(err)
                const {message} = err.response.data;
                let errMessage = "Não foi possível carregar.";

                if (I18nKeys[message]) {
                    errMessage = I18nKeys[message]
                    switch (item.type) {
                        case Types.INDIVIDUAL: errMessage = errMessage.replaceAll(":type", "CPF de interesse"); break;
                        case Types.VEHICLE: errMessage = errMessage.replaceAll(":type", "Placa de interesse"); break;
                        default: errMessage = errMessage.replaceAll(":type", "Identificador"); break;
                    }
                }

                projectUtils.swalErrorMessage("", errMessage, "Ok");
            })
            .finally(() => setIsLoading(false));
    }

    const inputLabel = {
        [Types.INDIVIDUAL]: "CPF de interesse",
        [Types.VEHICLE]: "Placa de interesse",
        [Types.ENCOUNTER]: "ID da abordagem",
        [Types.OCCURRENCE]: "ID da ocorrência",
        [Types.SOS]: "ID do SOS",
        [Types.BO]: "Número do " + boName,
        [Types.PASSAGE]: "Referência da passagem",
        [Types.NEWS]: "URL",
        [Types.SOCIAL_MEDIA]: "URL",
        [Types.COMPANY]: "CNPJ",
    }

    const setOpen = () => {
        setPanelOpened(null)
    }

    const selectFile = ({target: {files}}) => {

        if (files[0].size >= 2 * Math.pow(1024, 2)) {
            toast.warning('O arquivo não pode ter mais de 2Mb');
            return;
        }

        if (files && files[0]) {
            setImageFile(files[0])
        }

        setItem(prev => ({...prev, value: "image"}))
    }

    const gridColsByType = {
        [Types.NEWS]: "grid-cols-2!",
        [Types.IMAGE]: "grid-cols-2!",
        [Types.COMPANY]: "grid-cols-2!",
        [Types.SOCIAL_MEDIA]: "grid-cols-3!",
    }

    const gridCols = (type) => gridColsByType[type] ?? "grid-cols-1!"

    return (
        <>
            <Modal open={show} setOpen={setOpen} title={"Pesquisa"} className={"w-11/12 md:w-8/12 2xl:w-7/12"}>
                <Modal.Body className="p-2">
                    <SentryRow>
                        <div className={"col-span-12"}>
                            <div className="grid grid-cols-4 gap-4">
                                {
                                    items.map((i, index) => {
                                        return (
                                            <div key={index} onClick={() => setTabOnClick(i.id)}
                                                 className={`py-1 px-3 cursor-pointer border-2 rounded-md hover:border-[#676a6c] flex items-center gap-3 ${item.type === i.id ? "" : "border-[#f2f2f2]"}`}>

                                                <div className={"flex justify-center items-center h-12"}>
                                                    {
                                                        i.fontIcon
                                                            ? i.fontIcon
                                                            : <img
                                                                className={i.id === Types.VEHICLE ? "w-15.5 max-h-full object-contain" : "w-9.25 h-9.25 object-contain"}
                                                                src={getAssetPath(`img/dashboard-svg/${i.icon}`)}
                                                                alt="image"/>
                                                    }
                                                </div>
                                                <p className={"mb-0 font-bold text-center text-[14px]"}>{i.id === "bo" ? boName : i.name}</p>
                                            </div>
                                        )
                                    })
                                }
                            </div>
                        </div>
                        <div className={"col-span-12"}>
                            <SentryRow className={gridCols(item.type)}>
                                {
                                    item.type === Types.VEHICLE &&
                                    <InputForm
                                        key={item.type}
                                        reference={inputRef}
                                        inputClass={"text-upper"}
                                        id="plate"
                                        value={item.value}
                                        name={"plate"}
                                        label={inputLabel[item.type]}
                                        required
                                        onChange={onVehicleInputChange}
                                    />
                                }

                                {
                                    item.type === Types.INDIVIDUAL &&
                                    <InputForm
                                        key={item.type}
                                        reference={inputRef}
                                        id={"plate"}
                                        value={item.value}
                                        label={inputLabel[item.type]}
                                        inputClass={"text-upper"}
                                        required
                                        onChange={onChange}
                                        onBlur={onChange}
                                        mask={MaskDefault.cpf}
                                    />
                                }

                                {
                                    item.type === Types.COMPANY &&
                                    <>
                                        <InputForm
                                            key={item.type + "-identifier"}
                                            value={item.value}
                                            reference={inputRef}
                                            id={"cnpj"}
                                            label={inputLabel[item.type]}
                                            inputClass={"text-upper"}
                                            required
                                            onChange={onChange}
                                            onBlur={onChange}
                                            mask={MaskDefault.cnpj}
                                        />

                                        <InputForm
                                            key={item.type + "-description"}
                                            id="company-name"
                                            name={"company_name"}
                                            label={"Nome"}
                                            value={item.additional_value}
                                            onChange={onChangeAdditional}
                                        />
                                    </>
                                }

                                {
                                    item.type === Types.SOCIAL_MEDIA &&
                                    <>
                                        <SelectComponent
                                            key={item.type + "-network"}
                                            id={item.type + "-network"}
                                            inputId={item.type + "-network"}
                                            value={item.social_media}
                                            options={socialMedias}
                                            label={"Rede social"}
                                            required
                                            onChange={(selected) => setItem(prev => ({...prev, social_media: selected?.value}))}
                                        />

                                        <InputForm
                                            key={item.type + "-description"}
                                            id="description"
                                            value={item.additional_value}
                                            name={"description"}
                                            label={"Descrição"}
                                            onChange={onChangeAdditional}
                                        />
                                    </>
                                }

                                {
                                    [Types.IMAGE, Types.NEWS].includes(item.type) &&
                                    <InputForm
                                        key={item.type + "-description"}
                                        id="description"
                                        value={item.additional_value}
                                        name={"description"}
                                        label={"Descrição"}
                                        onChange={onChangeAdditional}
                                    />
                                }

                                {
                                    [Types.ANNOTATION].includes(item.type) &&
                                    <InputTextarea
                                        key={item.type}
                                        inputRef={inputRef}
                                        textAreaClass={"h-[100px]"}
                                        id="description-annotation"
                                        value={item.value}
                                        name={"description_annotation"}
                                        label={"Descrição"}
                                        required
                                        onChange={onChange}
                                    />
                                }

                                {
                                    ![Types.INDIVIDUAL, Types.VEHICLE, Types.COMPANY, Types.IMAGE, Types.ANNOTATION].includes(item.type) &&
                                    <InputForm
                                        key={item.type}
                                        reference={inputRef}
                                        id={"general-" + item.type}
                                        inputClass={"text-upper"}
                                        value={item.value}
                                        name={"general"}
                                        label={inputLabel[item.type]}
                                        required
                                        onChange={onChange}
                                    />
                                }

                                {
                                    item.type === Types.IMAGE &&
                                    <div
                                        ref={imageRequiredRef}
                                        tabIndex={-1}
                                        data-required-field="true"
                                        data-required-has-value={imageFile ? "true" : "false"}
                                        data-required-focus="true"
                                        className="flex h-8 w-full border border-gray-300 rounded-md overflow-hidden transition-[border-color,border-width] duration-150 ease-out"
                                    >
                                        <label className="w-26 p-1 flex items-center bg-gray-100 border-r border-gray-300 text-[11px] text-gray-700 cursor-pointer hover:bg-gray-200">
                                            Selecione arquivo<span className="ml-0.5">*</span>
                                            <input type="file" className="hidden" onChange={selectFile}/>
                                        </label>
                                        <div className="flex items-center text-[11px] p-1 text-gray-500 w-[calc(100%-104px)]">
                                            <span className={"truncate"}>{imageFile?.name}</span>
                                        </div>
                                    </div>
                                }
                            </SentryRow>
                        </div>
                    </SentryRow>
                </Modal.Body>

                <Modal.Footer className={"p-1"}>
                    <div className={"flex justify-end gap-2"}>
                        <Button onClick={() => init()}>Adicionar</Button>
                    </div>
                </Modal.Footer>
            </Modal>
        </>
    );
}

export default Search;